import React, { useState } from "react";
import type { Theme } from "../lib/theme";
import { buildChangeNarrative } from "../lib/changeNarrative";
import ChangeNarrativeModal from "../components/ChangeNarrativeModal";
import ImportVersionDialog from "../components/ImportVersionDialog";
import type { Division, ShowToast } from "../types";
import * as Icon from "../components/OEIcons";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

interface HistoryTabProps {
  T: Theme;
  isMobile: boolean;
  isTablet: boolean;
  projectSnapshots: any[];
  deletedSnapshots: any[];
  setDeletedSnapshots: React.Dispatch<React.SetStateAction<any[]>>;
  summarizeState: (state: any) => Record<string, {code: string; label: string; total: number}>;
  currentSummary: Record<string, any>;
  analysisOptions: any[];
  analysisLeft: string;
  setAnalysisLeft: React.Dispatch<React.SetStateAction<string>>;
  analysisRight: string | null;
  setAnalysisRight: React.Dispatch<React.SetStateAction<string | null>>;
  snapPreviewId: string | null;
  setSnapPreviewId: React.Dispatch<React.SetStateAction<string | null>>;
  renamingSnapId: string | null;
  setRenamingSnapId: React.Dispatch<React.SetStateAction<string | null>>;
  renameSnapLabel: string;
  setRenameSnapLabel: React.Dispatch<React.SetStateAction<string>>;
  renameSnapDate: string;
  setRenameSnapDate: React.Dispatch<React.SetStateAction<string>>;
  deltaPopupId: string | null;
  setDeltaPopupId: React.Dispatch<React.SetStateAction<string | null>>;
  deltaPopupPos: {x: number; top: number; bottom: number};
  setDeltaPopupPos: React.Dispatch<React.SetStateAction<{x: number; top: number; bottom: number}>>;
  showToast: ShowToast;
  setShowSnapDialog: React.Dispatch<React.SetStateAction<boolean>>;
  deleteSnapshot: (id: string) => Promise<void>;
  renameSnapshot: (id: string, newLabel: string, newDate?: string) => Promise<void>;
  restoreSnapshot: (id: string) => Promise<void>;
  handleOpenArchive: (snap: any) => void;
  getVersionSummary: (id: string) => any;
  getVersionTotal: (summary: any) => number;
  getVersionLabel: (id: string | null) => string;
  projectOwnerId: string | null;
  user?: {id: string; email: string; displayName: string; avatarUrl?: string} | null;
  fmt: (n: number) => string;
  collapsedAnalysisDivs: Set<string>;
  setCollapsedAnalysisDivs: React.Dispatch<React.SetStateAction<Set<string>>>;
  historyNotes: Record<string, string>;
  projectName: string;
  /** Whose cost codes an imported estimate is mapped onto. */
  currentOrgId: string | null;
  /** Files a parsed estimate into this project's history. */
  importVersion: (state: any, label: string, dateISO: string) => void;
  setHistoryNotes: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  divisions: Division[];
  showDeletedSnaps: boolean;
  setShowDeletedSnaps: React.Dispatch<React.SetStateAction<boolean>>;
  selectedArchivedIds: Set<string>;
  setSelectedArchivedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setConfirmAction: React.Dispatch<React.SetStateAction<any>>;
  setConfirmInput: React.Dispatch<React.SetStateAction<string>>;
  cloudStorage?: any;
  setLineItemPopup: React.Dispatch<React.SetStateAction<{code: string; label: string; x: number; top: number; bottom: number} | null>>;
}

export default function HistoryTab({
  T, isMobile, isTablet,
  projectSnapshots, deletedSnapshots, setDeletedSnapshots,
  summarizeState, currentSummary, analysisOptions,
  analysisLeft, setAnalysisLeft, analysisRight, setAnalysisRight,
  snapPreviewId, setSnapPreviewId,
  renamingSnapId, setRenamingSnapId, renameSnapLabel, setRenameSnapLabel,
  renameSnapDate, setRenameSnapDate,
  deltaPopupId, setDeltaPopupId, deltaPopupPos, setDeltaPopupPos,
  showToast, setShowSnapDialog,
  deleteSnapshot, renameSnapshot, restoreSnapshot, handleOpenArchive,
  getVersionSummary, getVersionTotal, getVersionLabel,
  projectOwnerId, user, fmt,
  collapsedAnalysisDivs, setCollapsedAnalysisDivs,
  historyNotes, setHistoryNotes, projectName, currentOrgId, importVersion,
  divisions,
  showDeletedSnaps, setShowDeletedSnaps,
  selectedArchivedIds, setSelectedArchivedIds,
  setConfirmAction, setConfirmInput,
  cloudStorage, setLineItemPopup,
}: HistoryTabProps) {
  const [showNarrative, setShowNarrative] = useState(false);
  const [showImport, setShowImport] = useState(false);
const allSnapSummaries=projectSnapshots.map(s=>({id:s.id,label:s.label,date:s.date,summary:summarizeState(s.state)}));
return (
  <div className="fade-in" style={{padding:isMobile?"14px 16px":isTablet?"20px 24px":"28px 36px",maxWidth:1100}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24,flexWrap:"wrap",gap:12}}>
      <div>
        <div style={{fontSize:24,fontWeight:700,color:T.borderAccent,marginBottom:4}}>Estimate History</div>
        <div style={{fontSize:11,color:T.textWeak}}>Save snapshots and compare any two versions side by side.</div>
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        {/* A project whose history starts today has no history. This is how
            the versions that already exist get in. */}
        <button onClick={()=>setShowImport(true)}
          title="Load an estimate you already have in as a past version"
          style={{background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textMuted,cursor:"pointer",padding:"6px 14px",borderRadius:3,fontSize:11,fontFamily:FONT,fontWeight:600,display:"inline-flex",alignItems:"center",gap:6}}
          onMouseEnter={e=>{e.currentTarget.style.borderColor=T.borderAccent;e.currentTarget.style.color=T.borderAccent;}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor=T.borderSecondary;e.currentTarget.style.color=T.textMuted;}}>
          <Icon.Upload size={12}/> Import Past Version
        </button>
        <button onClick={()=>setShowSnapDialog(true)}
          style={{background:T.bgAccentSubtle,border:`1px solid ${T.borderAccent}55`,color:T.borderAccent,cursor:"pointer",padding:"6px 14px",borderRadius:3,fontSize:11,fontFamily:FONT,fontWeight:600}}>
          + Save Milestone Snapshot
        </button>
      </div>
    </div>

    {showImport&&(
      <ImportVersionDialog
        T={T} FONT={FONT} orgId={currentOrgId}
        onClose={()=>setShowImport(false)}
        onImport={importVersion}
        showToast={showToast}
      />
    )}
    {projectSnapshots.length===0?(
      <div style={{background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:6,padding:"40px",textAlign:"center"}}>
        <div style={{fontSize:14,color:T.textMuted,marginBottom:8}}>No snapshots yet</div>
        <div style={{fontSize:11,color:T.textWeak,lineHeight:1.6}}>
          Save the current estimate as a snapshot to start tracking changes over time &mdash;
          or <button onClick={()=>setShowImport(true)} style={{background:"none",border:"none",color:T.borderAccent,cursor:"pointer",font:"inherit",padding:0,textDecoration:"underline"}}>import a past version</button> you already have as a spreadsheet.
        </div>
      </div>
    ):(()=>{
      // Build unified timeline: oldest→newest snapshots + "Current" cap
      const pts=[...allSnapSummaries.slice().reverse(),{id:"current",label:"Current",date:null,summary:currentSummary}];
      const totals:number[]=pts.map(p=>Object.values(p.summary).reduce<number>((a,v:any)=>a+(v?.total||0),0));
      const maxVal=Math.max(...totals.map(Math.abs),1);
      const curTotal=totals[totals.length-1];
      return(
        <div style={{background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:6,padding:"24px 28px",overflowX:"auto"}}>
          {/* Fixed-height bar zone (100px) + shared baseline + metadata below */}
          {(()=>{
            const BAR_ZONE=100; // px — fixed for all columns, baseline at bottom
            return(
              <div style={{display:"flex",gap:0,minWidth:pts.length*160}}>
                {pts.map((pt,i)=>{
                  const total=totals[i];
                  const isCur=pt.id==="current";
                  const barH=Math.max((Math.abs(total)/maxVal)*(BAR_ZONE-20),4); // leave 20px headroom for label
                  const delta=!isCur?curTotal-total:null;
                  const isRenaming=renamingSnapId===pt.id;
                  const hasNext=i<pts.length-1;
                  const barColor=isCur?T.borderAccent:total<0?T.negative:T.versionA.bar;
                  return(
                    <div key={pt.id} style={{display:"flex",flex:1,minWidth:160}}>
                      {/* Column */}
                      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center"}}>
                        {/* Fixed-height bar zone — bar grows up from baseline */}
                        <div style={{position:"relative",width:"100%",height:BAR_ZONE,flexShrink:0,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
                          {/* Amount label — floats just above bar */}
                          <div style={{position:"absolute",bottom:barH+5,left:0,right:0,textAlign:"center",
                            fontSize:10,fontWeight:700,color:isCur?T.borderAccent:total<0?T.negative:T.positive,whiteSpace:"nowrap",pointerEvents:"none"}}>
                            {fmt(total)}
                          </div>
                          {/* Bar */}
                          <div onClick={()=>{if(!isCur)setSnapPreviewId(pt.id);}}
                            title={isCur?undefined:"Click for quick view"}
                            style={{width:40,height:barH,background:barColor,borderRadius:"3px 3px 0 0",
                              cursor:isCur?"default":"pointer",opacity:isCur?1:.85,transition:"opacity .15s,filter .15s",flexShrink:0}}/>
                        </div>
                        {/* Shared baseline — always at same Y */}
                        <div style={{width:"100%",height:2,background:T.borderSecondary,flexShrink:0}}/>
                        {/* Delta chip */}
                        <div style={{height:20,display:"flex",alignItems:"center",justifyContent:"center",marginTop:5}}>
                          {delta!==null&&delta!==0&&(
                            <span onClick={e=>{e.stopPropagation();const r=(e.currentTarget as HTMLElement).getBoundingClientRect();setDeltaPopupPos({x:r.left+r.width/2,top:r.top,bottom:r.bottom});setDeltaPopupId(p=>p===pt.id?null:pt.id);}}
                              style={{fontSize:10,fontWeight:600,color:delta>0?T.negative:T.positive,cursor:"pointer",
                              background:delta>0?`${T.negative}18`:`${T.positive}18`,padding:"1px 6px",borderRadius:10,whiteSpace:"nowrap",
                              textDecoration:"underline",textDecorationStyle:"dotted",textUnderlineOffset:2}}>
                              {delta>0?"+":""}{fmt(delta)}
                            </span>
                          )}
                          {delta===0&&<span style={{fontSize:10,color:T.textWeak}}>no change</span>}
                        </div>
                        {/* Label + date */}
                        <div style={{marginTop:4,textAlign:"center",padding:"0 6px",width:"100%"}}>
                          {isRenaming?(
                            <div style={{display:"flex",flexDirection:"column",gap:4,minWidth:140}}>
                              <input autoFocus className="input-field" value={renameSnapLabel} onChange={e=>setRenameSnapLabel(e.target.value)}
                                onKeyDown={e=>{if(e.key==="Enter")renameSnapshot(pt.id,renameSnapLabel,renameSnapDate);if(e.key==="Escape"){setRenamingSnapId(null);setRenameSnapLabel("");setRenameSnapDate("");}}}
                                style={{fontSize:10,padding:"3px 6px",textAlign:"center"}}/>
                              <input type="date" className="input-field" value={renameSnapDate} onChange={e=>setRenameSnapDate(e.target.value)}
                                style={{fontSize:10,padding:"3px 6px",colorScheme:"dark"}}/>
                              <div style={{display:"flex",gap:4,justifyContent:"center"}}>
                                <button onClick={()=>renameSnapshot(pt.id,renameSnapLabel,renameSnapDate)}
                                  style={{background:T.bgAccentSubtle,border:`1px solid ${T.borderAccent}55`,color:T.borderAccent,cursor:"pointer",padding:"2px 8px",borderRadius:3,fontSize:10,fontFamily:FONT}}>Save</button>
                                <button onClick={()=>{setRenamingSnapId(null);setRenameSnapLabel("");setRenameSnapDate("");}}
                                  style={{background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textMuted,cursor:"pointer",padding:"2px 8px",borderRadius:3,fontSize:10,fontFamily:FONT}}><Icon.X size={10}/></button>
                              </div>
                            </div>
                          ):(
                            <>
                              <div style={{fontSize:11,fontWeight:isCur?700:600,color:isCur?T.borderAccent:T.textSecondary,lineHeight:1.3,wordBreak:"break-word"}}>{pt.label}</div>
                              {pt.date&&<div style={{fontSize:10,color:T.textMuted,marginTop:2}}>{new Date(pt.date).toLocaleDateString(undefined,{month:"short",day:"numeric",year:"numeric"})}</div>}
                              {isCur&&<div style={{fontSize:10,color:T.borderAccent,marginTop:2}}>live estimate</div>}
                            </>
                          )}
                        </div>
                        {/* Action buttons — snapshots only */}
                        {!isCur&&!isRenaming&&(
                          <div style={{display:"flex",gap:4,marginTop:8,justifyContent:"center",flexWrap:"wrap"}}>
                            <button onClick={()=>setSnapPreviewId(pt.id)}
                              style={{background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textMuted,cursor:"pointer",padding:"2px 7px",borderRadius:3,fontSize:10,fontFamily:FONT}}>Summary</button>
                            <button onClick={()=>handleOpenArchive(pt)}
                              style={{background:T.bgAccentSubtle,border:`1px solid ${T.borderAccent}55`,color:T.borderAccent,cursor:"pointer",padding:"2px 7px",borderRadius:3,fontSize:10,fontFamily:FONT,fontWeight:600}}>🔒 Open</button>
                            <button onClick={()=>{setAnalysisLeft(pt.id);setAnalysisRight("current");setTimeout(()=>document.getElementById("history-comparison")?.scrollIntoView({behavior:"smooth",block:"start"}),80);}}
                              style={{background:"none",border:`1px solid ${T.versionA.bar}44`,color:T.versionA.bar,cursor:"pointer",padding:"2px 7px",borderRadius:3,fontSize:10,fontFamily:FONT}}>Compare</button>
                            <button onClick={()=>{setRenamingSnapId(pt.id);setRenameSnapLabel(pt.label);setRenameSnapDate(pt.date?pt.date.slice(0,10):"");}}
                              style={{background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textMuted,cursor:"pointer",padding:"2px 7px",borderRadius:3,fontSize:10,fontFamily:FONT}}>Edit</button>
                            {(projectOwnerId===user?.id||!projectOwnerId)&&(
                              <button onClick={()=>deleteSnapshot(pt.id)}
                                title="Archive snapshot (owner only)"
                                style={{background:"none",border:`1px solid ${T.dangerBorder}`,color:T.dangerText,cursor:"pointer",padding:"2px 7px",borderRadius:3,fontSize:10,fontFamily:FONT}}>Archive</button>
                            )}
                          </div>
                        )}
                      </div>
                      {/* Connector arrow — vertically centred on the baseline */}
                      {hasNext&&(
                        <div style={{flexShrink:0,width:24,display:"flex",alignItems:"flex-start",paddingTop:BAR_ZONE,color:T.textWeak,fontSize:13,lineHeight:"2px"}}>→</div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      );
    })()}

    {/* ── VERSION COMPARISON (within History) ── */}
    <div id="history-comparison"/>
    {projectSnapshots.length>0&&(()=>{
      const leftSum = analysisLeft ? getVersionSummary(analysisLeft) : null;
      const rightSum = analysisRight ? getVersionSummary(analysisRight) : null;
      const leftTotal = leftSum ? getVersionTotal(leftSum) : 0;
      const rightTotal = rightSum ? getVersionTotal(rightSum) : null;
      const allCodes = leftSum && rightSum
        ? Array.from(new Set([...Object.keys(leftSum),...Object.keys(rightSum)])).sort()
        : leftSum ? Object.keys(leftSum).sort() : [];

      return (
        <div style={{marginTop:36,borderTop:`1px solid ${T.borderPrimary}`,paddingTop:28}}>
          <div style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:20}}>
            <div style={{flex:1}}>
              <div style={{fontSize:16,fontWeight:700,color:T.borderAccent,marginBottom:4}}>Version Comparison</div>
              <div style={{fontSize:11,color:T.textWeak}}>Tag any two versions <span style={{color:T.versionA.bar,fontWeight:600}}>A</span> and <span style={{color:T.versionB.bar,fontWeight:600}}>B</span> to compare &mdash; delta is always B&nbsp;&minus;&nbsp;A.</div>
            </div>
            {leftSum&&rightSum&&(
              <button onClick={()=>setShowNarrative(true)}
                title="Draft an owner-facing explanation of what moved and why"
                style={{flexShrink:0,background:"none",border:`1px solid ${T.borderAccent}66`,color:T.borderAccent,
                  borderRadius:3,padding:"7px 14px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:FONT,
                  display:"inline-flex",alignItems:"center",gap:6}}>
                <Icon.FileText size={13}/> Owner Summary
              </button>
            )}
          </div>

          {/* ── Single unified version list ── */}
          <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:28,width:"calc(66.667% - 5.33px)"}}>
            {analysisOptions.map(opt=>{
              const isA=analysisLeft===opt.id;
              const isB=analysisRight===opt.id;
              const rowBg=isA?T.versionA.bg:isB?T.versionB.bg:T.bgPanel;
              const rowBorder=isA?`${T.versionA.bar}55`:isB?`${T.versionB.bar}55`:T.borderPrimary;
              const Pill=({letter,active,activeColor,muted,onClick}:{letter:string;active:boolean;activeColor:string;muted?:boolean;onClick:()=>void})=>(
                <button onClick={onClick}
                  style={{width:22,height:22,borderRadius:3,border:`1.5px solid ${active?activeColor:T.borderSecondary}`,
                    background:active?activeColor:"transparent",color:active?"#fff":T.textWeak,
                    fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:FONT,lineHeight:1,
                    transition:"all .12s",flexShrink:0,opacity:muted?0.3:1}}>
                  {letter}
                </button>
              );
              return(
                <div key={opt.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",borderRadius:3,
                  background:rowBg,border:`1px solid ${rowBorder}`,transition:"all .15s"}}>
                  {/* A/B pills */}
                  <div style={{display:"flex",gap:4,flexShrink:0}}>
                    <Pill letter="A" active={isA} activeColor={T.versionA.bar} muted={!isA&&isB}
                      onClick={()=>{if(isA){setAnalysisLeft("");}else{setAnalysisLeft(opt.id);if(analysisRight===opt.id)setAnalysisRight("");}}}/>
                    <Pill letter="B" active={isB} activeColor={T.versionB.bar} muted={!isB&&isA}
                      onClick={()=>{if(isB){setAnalysisRight("");}else{setAnalysisRight(opt.id);if(analysisLeft===opt.id)setAnalysisLeft("");}}}/>
                  </div>
                  {/* Label + date */}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:isA||isB?600:400,
                      color:isA?T.versionA.bar:isB?T.versionB.bar:T.textSecondary,
                      whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{opt.label}</div>
                    {opt.date&&<div style={{fontSize:10,color:T.textMuted,marginTop:1}}>{new Date(opt.date).toLocaleDateString(undefined,{year:"numeric",month:"short",day:"numeric"})}</div>}
                    {opt.isLive&&<div style={{fontSize:10,color:T.borderAccent,marginTop:1}}>live estimate</div>}
                  </div>
                  {/* Total */}
                  <div style={{fontSize:13,fontWeight:600,color:isA?T.versionA.bar:isB?T.versionB.bar:T.textMuted,
                    fontVariantNumeric:"tabular-nums",flexShrink:0}}>{fmt(getVersionTotal(opt.summary))}</div>
                </div>
              );
            })}
          </div>

          {/* ── Summary cards ── */}
          {leftSum&&(
            <div style={{display:"flex",gap:16,marginBottom:28,flexWrap:"wrap"}}>
              <div style={{background:T.versionA.bg,border:`1px solid ${T.versionA.bar}33`,borderRadius:3,padding:"14px 20px",flex:1,minWidth:160}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                  <span style={{width:18,height:18,borderRadius:3,background:T.versionA.bar,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:"#fff",flexShrink:0}}>A</span>
                  <div style={{fontSize:10,color:T.textMuted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{getVersionLabel(analysisLeft)}</div>
                </div>
                <div style={{fontSize:20,fontWeight:700,color:leftTotal<0?T.negative:T.versionA.bar}}>{fmt(leftTotal)}</div>
              </div>
              {rightSum?(
                <>
                  <div style={{background:T.versionB.bg,border:`1px solid ${T.versionB.bar}33`,borderRadius:3,padding:"14px 20px",flex:1,minWidth:160}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                      <span style={{width:18,height:18,borderRadius:3,background:T.versionB.bar,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:"#fff",flexShrink:0}}>B</span>
                      <div style={{fontSize:10,color:T.textMuted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{getVersionLabel(analysisRight)}</div>
                    </div>
                    <div style={{fontSize:20,fontWeight:700,color:rightTotal<0?T.negative:T.versionB.bar}}>{fmt(rightTotal)}</div>
                  </div>
                  <div style={{background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:3,padding:"14px 20px",flex:1,minWidth:160}}>
                    <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",marginBottom:8}}>B &minus; A</div>
                    {(()=>{
                      const d=rightTotal-leftTotal,pct=leftTotal!==0?(d/Math.abs(leftTotal))*100:0;
                      return <>
                        <div style={{fontSize:20,fontWeight:700,color:d>0?T.negative:d<0?T.positive:T.textWeak}}>{d!==0?(d>0?"+":"")+fmt(d):"No change"}</div>
                        {d!==0&&<div style={{fontSize:12,color:d>0?T.negative:T.positive,marginTop:4}}>{(pct>0?"+":"")+pct.toFixed(0)}%</div>}
                      </>;
                    })()}
                  </div>
                </>
              ):(
                <div style={{background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:3,padding:"14px 20px",flex:2,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <div style={{textAlign:"center"}}>
                    <div style={{fontSize:12,color:T.textMuted,marginBottom:4}}>Tag a <span style={{color:T.versionB.bar,fontWeight:600}}>B</span> version above to compare</div>
                    <div style={{fontSize:10,color:T.textWeak}}>Deltas will appear once both A and B are selected.</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {showNarrative&&leftSum&&rightSum&&(
            <ChangeNarrativeModal
              T={T} FONT={FONT} projectName={projectName}
              fromLabel={getVersionLabel(analysisLeft)}
              toLabel={analysisRight?getVersionLabel(analysisRight):""}
              narrative={buildChangeNarrative(
                leftSum, rightSum,
                code=>historyNotes[`${analysisLeft}__${analysisRight}__${code}`]||"",
              )}
              onClose={()=>setShowNarrative(false)}
              showToast={showToast}
            />
          )}

          {leftSum&&rightSum&&(()=>{
            // Build division-grouped matrix — group subcategory codes by CSI division
            type SubEntry={code:string;rawLabel:string;subName:string;L:number;R:number};
            type DivGroup={divCode:string;divName:string;L:number;R:number;subs:SubEntry[]};
            const divGroups:DivGroup[]=[];
            const divMap:Record<string,DivGroup>={};
            allCodes.forEach(code=>{
              const Lv=leftSum[code]||{total:0,label:`${code} — (not present)`};
              const Rv=rightSum[code]||{total:0};
              const rawLabel:string=Lv.label||(rightSum[code]?.label)||code;
              // Label format: "05-050 — Structural Steel (Div 05)"
              const divMatch=rawLabel.match(/\((Div\s*\d+)\)/);
              const prefix=code.split('-')[0]||"00";
              const divCode=divMatch?divMatch[1]:`Div ${prefix}`;
              // Division name from live divisions state
              const divObj=divisions.find((d:any)=>d.code===divCode);
              const divName=divObj?.name||divCode;
              // Sub display name: strip "XX-XXX — " prefix and " (Div XX)" suffix
              const subName=rawLabel.replace(/^\S+\s*—\s*/,"").replace(/\s*\([^)]+\)$/,"");
              if(!divMap[divCode]){
                const g:DivGroup={divCode,divName,L:0,R:0,subs:[]};
                divMap[divCode]=g;
                divGroups.push(g);
              }
              divMap[divCode].L+=Lv.total;
              divMap[divCode].R+=Rv.total;
              divMap[divCode].subs.push({code,rawLabel,subName,L:Lv.total,R:Rv.total});
            });
            divGroups.sort((a,b)=>(parseInt(a.divCode.replace(/\D/g,""))||0)-(parseInt(b.divCode.replace(/\D/g,""))||0));

            return (
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead>
                  <tr style={{borderBottom:`2px solid ${T.borderAccentFaint}`}}>
                    {[["Division / Subcategory","left","37%"],["Version A","right","14%"],["Version B","right","14%"],["Delta","right","12%"],["% Chg","right","7%"],["Notes","left","16%"]].map(([h,a,w])=>(
                      <th key={h} style={{textAlign:a as any,fontSize:10,color:T.textWeak,letterSpacing:".12em",padding:"8px 10px",fontWeight:500,textTransform:"uppercase",width:w}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {divGroups.flatMap(group=>{
                    const dk=group.divCode;
                    const isCollapsed=collapsedAnalysisDivs.has(dk);
                    const divDelta=group.R-group.L;
                    const divPct=group.L!==0?(divDelta/Math.abs(group.L))*100:group.R!==0?100:0;
                    const divRowBg=divDelta>0?`${T.dangerBg}18`:divDelta<0?`${T.versionB.bg}18`:T.bgHeader;
                    return [
                      // Division header row
                      <tr key={`div-${dk}`}
                        onClick={()=>setCollapsedAnalysisDivs(prev=>{const n=new Set(prev);n.has(dk)?n.delete(dk):n.add(dk);return n;})}
                        style={{cursor:"pointer",background:divRowBg,borderBottom:`1px solid ${T.borderPrimary}`,userSelect:"none"}}>
                        <td style={{padding:"10px 10px",fontSize:12,fontWeight:700,color:T.textPrimary}}>
                          <span style={{marginRight:8,fontSize:10,color:T.textWeak,display:"inline-block",width:10}}>{isCollapsed?"▸":"▾"}</span>
                          <span style={{marginRight:8,fontSize:10,color:T.textMuted,fontFamily:"monospace",fontWeight:400}}>{dk}</span>
                          {group.divName}
                          <span style={{marginLeft:10,fontSize:10,color:T.textWeak,fontWeight:400,opacity:.7}}>{group.subs.length} {group.subs.length===1?"line":"lines"}</span>
                        </td>
                        <td style={{padding:"10px",textAlign:"right",fontSize:13,fontWeight:600,color:group.L<0?T.negative:T.versionA.bar}}>{group.L!==0?fmt(group.L):"--"}</td>
                        <td style={{padding:"10px",textAlign:"right",fontSize:13,fontWeight:600,color:group.R<0?T.negative:T.versionB.bar}}>{group.R!==0?fmt(group.R):"--"}</td>
                        <td style={{padding:"10px",textAlign:"right",fontSize:13,fontWeight:700,color:divDelta>0?T.negative:divDelta<0?T.positive:T.textMuted}}>{divDelta!==0?(divDelta>0?"+":"")+fmt(divDelta):"--"}</td>
                        <td style={{padding:"10px",textAlign:"right",fontSize:11,fontWeight:600,color:divPct>0?T.negative:divPct<0?T.positive:T.textMuted}}>{divDelta!==0?(divPct>0?"+":"")+divPct.toFixed(0)+"%":"--"}</td>
                        <td/>
                      </tr>,
                      // Subcategory rows
                      ...(!isCollapsed?group.subs.map(sub=>{
                        const delta=sub.R-sub.L;
                        const pct=sub.L!==0?(delta/Math.abs(sub.L))*100:sub.R!==0?100:0;
                        const nk=`${analysisLeft}__${analysisRight}__${sub.code}`;
                        const note=historyNotes[nk]||"";
                        const isNew=!leftSum[sub.code]&&sub.R!==0;
                        const isGone=!rightSum[sub.code]&&sub.L!==0;
                        const rowBg=isNew?T.versionB.bg:isGone?T.dangerBg:delta>0?T.dangerBg+"0a":delta<0?T.versionB.bg+"0a":"transparent";
                        return (
                          <tr key={sub.code} style={{borderBottom:`1px solid ${T.bgHeader}`,background:rowBg}}>
                            <td style={{padding:"7px 10px 7px 36px",fontSize:11,color:isGone?T.dangerText:isNew?T.positive:T.textSecondary}}>
                              <span style={{fontSize:10,color:T.textMuted,fontFamily:"monospace",letterSpacing:".06em",marginRight:8}}>{sub.code}</span>
                              <span
                                onClick={e=>{e.stopPropagation();const r=(e.currentTarget as HTMLElement).getBoundingClientRect();setLineItemPopup({code:sub.code,label:sub.rawLabel,x:r.left+r.width/2,top:r.top,bottom:r.bottom});}}
                                style={{cursor:"pointer",textDecoration:"underline",textDecorationStyle:"dotted",textUnderlineOffset:3,textDecorationColor:`${T.borderAccent}66`}}
                                title="Click to compare line items">{sub.subName}</span>
                              {isNew&&<span style={{marginLeft:6,fontSize:10,color:T.positive,background:T.versionB.bg,padding:"1px 6px",borderRadius:10}}>ONLY IN B</span>}
                              {isGone&&<span style={{marginLeft:6,fontSize:10,color:T.negative,background:T.dangerBg,padding:"1px 6px",borderRadius:10}}>ONLY IN A</span>}
                            </td>
                            <td style={{padding:"7px 10px",textAlign:"right",fontSize:12,color:sub.L<0?T.negative:T.versionA.bar}}>{sub.L!==0?fmt(sub.L):"--"}</td>
                            <td style={{padding:"7px 10px",textAlign:"right",fontSize:12,color:sub.R<0?T.negative:T.versionB.bar}}>{sub.R!==0?fmt(sub.R):"--"}</td>
                            <td style={{padding:"7px 10px",textAlign:"right",fontSize:12,fontWeight:600,color:delta>0?T.negative:delta<0?T.positive:T.textMuted}}>
                              {delta!==0?(
                                <span onClick={e=>{e.stopPropagation();const r=(e.currentTarget as HTMLElement).getBoundingClientRect();setLineItemPopup({code:sub.code,label:sub.rawLabel,x:r.left+r.width/2,top:r.top,bottom:r.bottom});}}
                                  style={{cursor:"pointer",textDecoration:"underline",textDecorationStyle:"dotted",textUnderlineOffset:2}}
                                  title="Click to see line-item breakdown">
                                  {(delta>0?"+":"")+fmt(delta)}
                                </span>
                              ):"--"}
                            </td>
                            <td style={{padding:"7px 10px",textAlign:"right",fontSize:11,color:pct>0?T.negative:pct<0?T.positive:T.textMuted}}>{delta!==0?(pct>0?"+":"")+pct.toFixed(0)+"%":"--"}</td>
                            <td style={{padding:"4px 6px"}}>
                              <input className="note-input" value={note} onChange={e=>setHistoryNotes(p=>({...p,[nk]:e.target.value}))} placeholder={delta!==0?"Add note...":""}/>
                            </td>
                          </tr>
                        );
                      }):[])
                    ];
                  })}
                </tbody>
                <tfoot>
                  {(()=>{
                    const d=rightTotal-leftTotal,pct=leftTotal!==0?(d/Math.abs(leftTotal))*100:0;
                    return <tr style={{borderTop:`2px solid ${T.borderAccentFaint}`}}>
                      <td style={{padding:"10px",fontSize:11,color:T.textMuted,letterSpacing:".08em",textTransform:"uppercase"}}>Total Direct Cost</td>
                      <td style={{padding:"10px",textAlign:"right",fontSize:13,fontWeight:600,color:leftTotal<0?T.negative:T.versionA.bar}}>{fmt(leftTotal)}</td>
                      <td style={{padding:"10px",textAlign:"right",fontSize:13,fontWeight:600,color:rightTotal<0?T.negative:T.versionB.bar}}>{fmt(rightTotal)}</td>
                      <td style={{padding:"10px",textAlign:"right",fontSize:14,fontWeight:700,color:d>0?T.negative:d<0?T.positive:T.textMuted}}>{d!==0?(d>0?"+":"")+fmt(d):"--"}</td>
                      <td style={{padding:"10px",textAlign:"right",fontSize:12,fontWeight:600,color:pct>0?T.negative:pct<0?T.positive:T.textMuted}}>{d!==0?(pct>0?"+":"")+pct.toFixed(0)+"%":"--"}</td>
                      <td/>
                    </tr>;
                  })()}
                </tfoot>
              </table>
            );
          })()}
        </div>
      );
    })()}

    {/* ── ARCHIVED SNAPSHOTS (owner only) ── */}
    {(projectOwnerId===user?.id||!projectOwnerId)&&(
      <div style={{marginTop:40,borderTop:`1px solid ${T.borderPrimary}`,paddingTop:28}}>
        {/* Section header */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}
            onClick={()=>{setShowDeletedSnaps(v=>!v);setSelectedArchivedIds(new Set());}}>
            <span style={{fontSize:11,color:T.textWeak,opacity:.6}}>{showDeletedSnaps?"▾":"▸"}</span>
            <span style={{fontSize:14,fontWeight:600,color:T.textWeak}}>Archived Snapshots</span>
            {deletedSnapshots.length>0&&(
              <span style={{fontSize:10,fontWeight:700,background:T.bgInput,border:`1px solid ${T.borderSecondary}`,
                borderRadius:10,padding:"1px 8px",color:T.textWeak}}>
                {deletedSnapshots.length}
              </span>
            )}
            <span style={{fontSize:10,color:T.textWeak,opacity:.6}}>— visible to project owners only</span>
          </div>
          {/* Batch action bar — only when items selected */}
          {showDeletedSnaps&&selectedArchivedIds.size>0&&(
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:11,color:T.textWeak}}>{selectedArchivedIds.size} selected</span>
              <button onClick={()=>{
                  const ids=Array.from(selectedArchivedIds);
                  const labels=deletedSnapshots.filter(s=>ids.includes(s.id)).map(s=>s.label).join(", ");
                  setConfirmAction({
                    title:`Permanently Delete ${ids.length} Snapshot${ids.length>1?"s":""}`,
                    message:`This will permanently destroy: ${labels}. This cannot be undone.`,
                    inputRequired:"DELETE",
                    onConfirm:async()=>{
                      for(const id of ids){
                        if(cloudStorage?.permanentlyDeleteSnapshotById)await cloudStorage.permanentlyDeleteSnapshotById(id);
                      }
                      setDeletedSnapshots(prev=>prev.filter(s=>!ids.includes(s.id)));
                      setSelectedArchivedIds(new Set());
                      setConfirmAction(null);setConfirmInput("");
                      showToast(`${ids.length} snapshot${ids.length>1?"s":""} permanently deleted`);
                    }
                  });
                }}
                style={{background:"none",border:`1px solid ${T.dangerBorder}`,color:T.dangerText,
                  cursor:"pointer",padding:"4px 12px",borderRadius:3,fontSize:11,fontFamily:FONT,fontWeight:600}}>
                Delete Selected
              </button>
              <button onClick={()=>setSelectedArchivedIds(new Set())}
                style={{background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textMuted,
                  cursor:"pointer",padding:"4px 10px",borderRadius:3,fontSize:11,fontFamily:FONT}}>
                Clear
              </button>
            </div>
          )}
        </div>
        {showDeletedSnaps&&(
          deletedSnapshots.length===0?(
            <div style={{background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:6,
              padding:"22px 24px",fontSize:11,color:T.textMuted,textAlign:"center"}}>
              No archived snapshots
            </div>
          ):(
            <div style={{background:T.bgPanel,border:`1px solid ${T.borderPrimary}`,borderRadius:6,overflow:"hidden"}}>
              {/* Select-all header row */}
              <div style={{display:"flex",alignItems:"center",gap:12,padding:"8px 20px",
                borderBottom:`1px solid ${T.borderPrimary}`,background:T.bgHeader}}>
                <input type="checkbox"
                  checked={selectedArchivedIds.size===deletedSnapshots.length&&deletedSnapshots.length>0}
                  ref={el=>{if(el)el.indeterminate=selectedArchivedIds.size>0&&selectedArchivedIds.size<deletedSnapshots.length;}}
                  onChange={e=>setSelectedArchivedIds(e.target.checked?new Set(deletedSnapshots.map(s=>s.id)):new Set())}
                  style={{cursor:"pointer",accentColor:T.borderAccent,flexShrink:0}}/>
                <span style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",flex:1}}>
                  {selectedArchivedIds.size>0?`${selectedArchivedIds.size} of ${deletedSnapshots.length} selected`:"Select all"}
                </span>
              </div>
              {deletedSnapshots.map((snap,i)=>{
                const snapTotal=snap.state?Object.values(summarizeState(snap.state)).reduce((a:number,v:any)=>a+v.total,0):0;
                const archivedDate=snap.deletedAt?new Date(snap.deletedAt).toLocaleDateString(undefined,{year:"numeric",month:"short",day:"numeric"}):"—";
                const createdDate=snap.date?new Date(snap.date).toLocaleDateString(undefined,{year:"numeric",month:"short",day:"numeric"}):"—";
                const isSelected=selectedArchivedIds.has(snap.id);
                return(
                  <div key={snap.id}
                    onClick={()=>setSelectedArchivedIds(prev=>{const n=new Set(prev);isSelected?n.delete(snap.id):n.add(snap.id);return n;})}
                    style={{display:"flex",alignItems:"center",gap:12,padding:"11px 20px",cursor:"pointer",
                      background:isSelected?T.bgAccentFaint:"transparent",
                      borderBottom:i<deletedSnapshots.length-1?`1px solid ${T.bgInput}`:"none",
                      transition:"background .1s"}}>
                    <input type="checkbox" checked={isSelected} readOnly
                      style={{cursor:"pointer",accentColor:T.borderAccent,flexShrink:0,pointerEvents:"none"}}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,fontWeight:500,color:isSelected?T.textSecondary:T.textTertiary}}>{snap.label}</div>
                      <div style={{fontSize:10,color:T.textWeak,marginTop:2}}>
                        Saved {createdDate} &nbsp;·&nbsp; Archived {archivedDate}
                      </div>
                    </div>
                    <div style={{fontSize:12,color:T.textMuted,fontVariantNumeric:"tabular-nums",flexShrink:0}}>{snapTotal?fmt(snapTotal):"—"}</div>
                    <div style={{display:"flex",gap:6,flexShrink:0}} onClick={e=>e.stopPropagation()}>
                      <button onClick={()=>handleOpenArchive(snap)}
                        style={{background:"none",border:`1px solid ${T.borderAccent}44`,color:T.borderAccent,
                          cursor:"pointer",padding:"3px 10px",borderRadius:3,fontSize:10,fontFamily:FONT,fontWeight:600}}>
                        🔒 Open
                      </button>
                      <button onClick={()=>restoreSnapshot(snap.id)}
                        style={{background:T.bgAccentSubtle,border:`1px solid ${T.borderAccent}55`,color:T.borderAccent,
                          cursor:"pointer",padding:"3px 10px",borderRadius:3,fontSize:10,fontFamily:FONT,fontWeight:600}}>
                        ↩ Restore
                      </button>
                      <button onClick={()=>setConfirmAction({
                          title:"Permanently Delete Snapshot",
                          message:`This will permanently destroy "${snap.label}". This cannot be undone.`,
                          inputRequired:"DELETE",
                          onConfirm:async()=>{
                            if(cloudStorage?.permanentlyDeleteSnapshotById)await cloudStorage.permanentlyDeleteSnapshotById(snap.id);
                            setDeletedSnapshots(prev=>prev.filter(s=>s.id!==snap.id));
                            setSelectedArchivedIds(prev=>{const n=new Set(prev);n.delete(snap.id);return n;});
                            setConfirmAction(null);setConfirmInput("");
                            showToast("Snapshot permanently deleted");
                          }
                        })}
                        style={{background:"none",border:`1px solid ${T.dangerBorder}`,color:T.dangerText,
                          cursor:"pointer",padding:"3px 10px",borderRadius:3,fontSize:10,fontFamily:FONT}}>
                        Delete Forever
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
    )}
  </div>
);

}
